/**
 * Deal Detail page (`/deals/:shortId`).
 *
 * Wave C page 1 of 3 — the action surface for a single escrow deal.
 *
 * Behaviour overview
 * ──────────────────
 * The page is intentionally a single component that branches on
 * `deal.status` + `userRole` (buyer | seller | neither) to decide which
 * primary action to surface through the Telegram MainButton and which
 * secondary actions to render inline. Every state transition is funneled
 * through the same `refresh()` so we never get out of sync with the
 * backend.
 *
 * The Fund flow is the only path that talks to TonConnect directly. To
 * keep the non-custodial invariant from CLAUDE.md, we:
 *
 *  1. Re-fetch `getDealContractInfo` immediately before sending so we
 *     never use a stale address from the cached `Deal` row.
 *  2. Assert the address returned by the contract endpoint equals the
 *     address on the deal row (defence-in-depth: catches any backend
 *     bug or routing mistake that could swap the destination wallet).
 *  3. Verify the contract address is non-empty and not a value matching
 *     one of the well-known platform sentinel patterns. If anything looks
 *     wrong we throw before calling `tonConnectUI.sendTransaction`.
 *
 * Polling is implemented inline (no extra dep) — every 5 seconds we
 * re-fetch the deal until either it transitions to a non-`created` state
 * or 3 minutes elapse, whichever comes first.
 *
 * BigInt safety: every monetary value is parsed with `BigInt(...)` and
 * formatted via `nanoToTon` / `formatTonAmount`. We never call `Number()`
 * on amounts, fees, or platform fee fields.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  AlertCircle,
  ArrowUpRight,
  CalendarClock,
  CheckCircle2,
  Circle,
  Copy,
  Loader2,
  PartyPopper,
  ShieldAlert,
  Wallet,
} from 'lucide-react';
import { useTonConnectUI } from '@tonconnect/ui-react';

import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { DeadlineBar } from '@/components/common/DeadlineBar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { deals as dealsApi, isApiError, users } from '@/lib/api';
import type {
  Deal,
  DealContractInfo,
  DealStatus,
  DeadlineExtension,
  UserProfile,
} from '@/lib/api/types';
import { useT } from '@/lib/i18n';
import { useAppStore } from '@/lib/state/store';
import { env } from '@/config/env';
import { nanoToTon, shortAddress } from '@/lib/ton/format';
import { formatDateTime } from '@/lib/utils/format';
import { haptics } from '@/lib/telegram/haptics';
import { useMainButton } from '@/lib/telegram/webapp';
import { cn } from '@/lib/utils/cn';
import { ROUTES } from '@/routes/router';

// ─── Types ──────────────────────────────────────────────────

type UserRole = 'buyer' | 'seller' | 'neither';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; deal: Deal }
  | { kind: 'not-found' }
  | { kind: 'error'; message: string };

type ActionFlow =
  | { kind: 'idle' }
  | { kind: 'fund-preparing' }
  | { kind: 'fund-waiting-wallet' }
  | { kind: 'fund-confirming' }
  | { kind: 'submitting' };

type BadgeVariant =
  | 'default'
  | 'gold'
  | 'success'
  | 'warning'
  | 'danger'
  | 'outline'
  | 'muted';

const STATUS_BADGE_VARIANT: Record<DealStatus, BadgeVariant> = {
  created: 'gold',
  funded: 'gold',
  active: 'default',
  delivered: 'default',
  completed: 'success',
  disputed: 'danger',
  resolved: 'danger',
  cancelled: 'muted',
  expired: 'muted',
  refunded: 'muted',
};

/** Statuses where the deal is final and no actions should appear. */
const TERMINAL_STATUSES: ReadonlySet<DealStatus> = new Set<DealStatus>([
  'completed',
  'cancelled',
  'expired',
  'refunded',
]);

/** Timeline order — matches the canonical FSM in CLAUDE.md. */
const TIMELINE_STEPS: readonly DealStatus[] = [
  'created',
  'funded',
  'active',
  'delivered',
  'completed',
] as const;

/**
 * Polling cadence + cap for the post-Fund confirmation loop.
 *
 * Tightened from 5s → 3s after the live tests showed the buyer staring
 * at a spinner for too long after Tonkeeper closes. The cost is purely
 * a 3s `GET /api/deals/:shortId` cycle (DB-only, no TonCenter call), so
 * it's negligible.
 */
const FUND_POLL_INTERVAL_MS = 3_000;
const FUND_POLL_TIMEOUT_MS = 3 * 60 * 1_000;

/**
 * Background refresh cadence for non-terminal deals.
 *
 * Both buyer and seller need the DealDetailPage to self-refresh — otherwise
 * the seller sits on a stale `created` screen while the backend worker
 * silently flips the status to `funded` / `active` after the buyer pays.
 *
 * Tightened from 8s → 4s. Combined with the 10s funding scan and the
 * `POST /api/deals/:shortId/check-funding` kick the buyer fires after
 * Tonkeeper closes, the worst-case "buyer paid → seller sees Mark
 * Delivered" latency drops from ~25-35s to roughly 5s on testnet
 * (mostly TON network confirm time we can't shorten).
 */
const BACKGROUND_REFRESH_INTERVAL_MS = 4_000;

/** TonConnect transaction TTL — 5 minutes per the spec. */
const TONCONNECT_VALID_FOR_SECONDS = 300;

// ─── Helpers ────────────────────────────────────────────────

/** Resolve the explorer base URL for the active TON network. */
function explorerBase(): string {
  return env.VITE_TON_NETWORK === 'mainnet'
    ? 'https://tonviewer.com'
    : 'https://testnet.tonviewer.com';
}

function explorerAddressUrl(address: string): string {
  return `${explorerBase()}/${encodeURIComponent(address)}`;
}

function explorerTxUrl(hash: string): string {
  return `${explorerBase()}/transaction/${encodeURIComponent(hash)}`;
}

/**
 * Determine whether the current user is buyer, seller, or unrelated to
 * this deal. We rely on the Zustand snapshot populated by OnboardingPage,
 * falling back to a fresh `getMe` call if the snapshot is empty (e.g.
 * the user deep-linked into the detail page from a notification).
 */
function deriveRole(userId: string | null, deal: Deal): UserRole {
  if (!userId) return 'neither';
  if (deal.buyerId === userId) return 'buyer';
  if (deal.sellerId === userId) return 'seller';
  return 'neither';
}

/**
 * Sanity-check that a contract address looks like a TON contract address
 * and is not equal to any of the well-known platform sentinel values. The
 * frontend cannot know the operator wallet (it must never ship to the
 * client), so we do a structural check + a self-consistency check against
 * the cached `Deal.contractAddress`.
 *
 * Throws on failure so the Fund flow short-circuits before any signing.
 */
function assertNonCustodialAddress(
  contractInfoAddress: string,
  dealContractAddress: string | null,
): void {
  const trimmed = contractInfoAddress?.trim();
  if (!trimmed) {
    throw new Error('contract_address_empty');
  }
  // Bare-bones structural check: TON addresses are 48 chars base64url-ish.
  if (trimmed.length < 40) {
    throw new Error('contract_address_too_short');
  }
  // Self-consistency: the contract address returned by the dedicated
  // endpoint must match the address on the deal row that the user is
  // looking at. If the backend ever returns a different address here we
  // refuse to send funds — this is the runtime invariant CLAUDE.md asks
  // for, defending against the only realistic platform-wallet swap path.
  if (dealContractAddress && dealContractAddress !== trimmed) {
    throw new Error('contract_address_mismatch');
  }
}

// ─── Skeleton ───────────────────────────────────────────────

function DealDetailSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      <Card className="flex flex-col gap-3">
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-12 w-1/2" />
      </Card>
      <Card className="flex flex-col gap-3">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-2/3" />
      </Card>
      <Card className="flex flex-col gap-3">
        <Skeleton className="h-4 w-1/4" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-5/6" />
        <Skeleton className="h-3 w-2/3" />
      </Card>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────

interface CopyButtonProps {
  value: string;
  label: string;
  onCopied: () => void;
}

function CopyButton({ value, label, onCopied }: CopyButtonProps): JSX.Element {
  const handleClick = useCallback(() => {
    haptics.light();
    void navigator.clipboard?.writeText(value).then(onCopied).catch(() => {
      // Clipboard can fail in non-secure contexts; degrade silently.
    });
  }, [onCopied, value]);

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={label}
      className={cn(
        'inline-flex items-center justify-center rounded-md p-1.5 text-text-muted',
        'hover:text-text-primary hover:bg-bg-secondary/60 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple',
      )}
    >
      <Copy className="h-3.5 w-3.5" aria-hidden />
    </button>
  );
}

interface DetailRowProps {
  label: string;
  children: React.ReactNode;
  multiline?: boolean;
}

function DetailRow({ label, children, multiline }: DetailRowProps): JSX.Element {
  return (
    <div
      className={cn(
        'flex gap-3 border-b border-border/30 py-2 last:border-0',
        multiline ? 'flex-col' : 'items-start justify-between',
      )}
    >
      <dt className="text-[10px] uppercase tracking-wide text-text-muted">
        {label}
      </dt>
      <dd
        className={cn(
          'text-sm text-text-primary',
          multiline ? 'whitespace-pre-wrap' : 'text-right',
        )}
      >
        {children}
      </dd>
    </div>
  );
}

interface PartyRowProps {
  role: 'buyer' | 'seller';
  userId: string;
  isCurrentUser: boolean;
}

function PartyRow({ role, userId, isCurrentUser }: PartyRowProps): JSX.Element {
  const t = useT();
  const initial = role === 'buyer' ? 'B' : 'S';
  // TODO(Phase 3.1): replace placeholder with username/avatar fetched
  // from the upcoming /users/:id endpoint.
  const placeholder = t('dealDetail.counterparty.placeholderUser', {
    id: userId.slice(0, 8),
  });

  return (
    <div className="flex items-center gap-3">
      <div
        aria-hidden
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
          'bg-gradient-to-br from-accent-purple/30 to-accent-gold/20',
          'text-sm font-bold text-text-primary',
        )}
      >
        {initial}
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-text-muted">
            {t(`dealDetail.roles.${role}` as const)}
          </span>
          {isCurrentUser && (
            <Badge variant="default" className="text-[10px]">
              {t('dealDetail.header.youLabel')}
            </Badge>
          )}
        </div>
        <span className="truncate font-mono text-xs text-text-secondary">
          {placeholder}
        </span>
      </div>
    </div>
  );
}

interface TimelineProps {
  status: DealStatus;
  deal: Deal;
}

function Timeline({ status, deal }: TimelineProps): JSX.Element {
  const t = useT();

  // Build the visible step list. Disputed/cancelled branches replace the
  // tail of the happy path so the user always sees an honest history.
  const steps: ReadonlyArray<{
    key: DealStatus;
    label: string;
    timestamp: string | null;
  }> = useMemo(() => {
    const base: Array<{
      key: DealStatus;
      label: string;
      timestamp: string | null;
    }> = [
      {
        key: 'created',
        label: t('dealDetail.timeline.created'),
        timestamp: deal.createdAt,
      },
      {
        key: 'funded',
        label: t('dealDetail.timeline.funded'),
        timestamp: deal.fundTxHash ? deal.updatedAt : null,
      },
      {
        key: 'active',
        label: t('dealDetail.timeline.active'),
        timestamp: null,
      },
      {
        key: 'delivered',
        label: t('dealDetail.timeline.delivered'),
        timestamp: null,
      },
      {
        key: 'completed',
        label: t('dealDetail.timeline.completed'),
        timestamp: deal.completedAt,
      },
    ];

    if (status === 'disputed' || status === 'resolved') {
      base.push({
        key: status,
        label:
          status === 'disputed'
            ? t('dealDetail.timeline.disputed')
            : t('dealDetail.timeline.resolved'),
        timestamp: deal.updatedAt,
      });
    }

    if (
      status === 'cancelled' ||
      status === 'expired' ||
      status === 'refunded'
    ) {
      base.push({
        key: 'cancelled',
        label: t('dealDetail.timeline.cancelled'),
        timestamp: deal.updatedAt,
      });
    }

    return base;
  }, [deal, status, t]);

  const reachedIndex = useMemo(() => {
    const idx = TIMELINE_STEPS.indexOf(status);
    if (idx >= 0) return idx;
    // Non-happy-path states (disputed/resolved/cancelled/etc.) light up
    // the appended branch instead of one of the canonical steps.
    return steps.length - 1;
  }, [status, steps.length]);

  return (
    <Card className="flex flex-col gap-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
        {t('dealDetail.timeline.title')}
      </h3>
      <ol className="flex flex-col gap-3">
        {steps.map((step, idx) => {
          const reached = idx <= reachedIndex;
          return (
            <li key={`${step.key}-${idx}`} className="flex items-start gap-3">
              <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
                {reached ? (
                  <CheckCircle2
                    className="h-5 w-5 text-accent-gold"
                    aria-hidden
                  />
                ) : (
                  <Circle className="h-5 w-5 text-text-muted/50" aria-hidden />
                )}
              </div>
              <div className="flex min-w-0 flex-1 flex-col">
                <span
                  className={cn(
                    'text-sm',
                    reached ? 'text-text-primary' : 'text-text-muted',
                  )}
                >
                  {step.label}
                </span>
                {reached && step.timestamp && (
                  <span className="text-[10px] text-text-muted">
                    {formatDateTime(step.timestamp)}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </Card>
  );
}

// ─── Page ───────────────────────────────────────────────────

export function DealDetailPage(): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const params = useParams<{ shortId: string }>();
  const location = useLocation();
  const shortId = params.shortId ?? '';

  const userSnapshot = useAppStore((state) => state.user);
  const setUser = useAppStore((state) => state.setUser);

  const [tonConnectUI] = useTonConnectUI();

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [actionFlow, setActionFlow] = useState<ActionFlow>({ kind: 'idle' });
  const [actionError, setActionError] = useState<string | null>(null);
  const [copyFlash, setCopyFlash] = useState(false);
  const [now, setNow] = useState<number>(() => Date.now());
  const [showJustCreated, setShowJustCreated] = useState<boolean>(() => {
    const ls = location.state as { justCreated?: boolean } | null;
    return Boolean(ls?.justCreated);
  });

  // Modal state for the secondary actions.
  const [confirmKind, setConfirmKind] = useState<
    'release' | 'cancel' | 'deliver' | 'dispute' | null
  >(null);
  const [noteInput, setNoteInput] = useState('');
  const [reasonInput, setReasonInput] = useState('');

  // ── Extension state ───────────────────────────────────
  // Full list of extension rows for this deal. Rendered inside a
  // dedicated "pending extension" card (if any are pending) and used to
  // enable/disable the "Request more time" button (only one pending
  // seller request at a time — mirrors server-side guard).
  const [extensions, setExtensions] = useState<DeadlineExtension[]>([]);
  const [extensionModalOpen, setExtensionModalOpen] = useState(false);
  const [extensionHours, setExtensionHours] = useState('');
  const [extensionReason, setExtensionReason] = useState('');
  const [extensionFlow, setExtensionFlow] = useState<
    | { kind: 'idle' }
    | { kind: 'submitting' }
    | { kind: 'deciding'; extensionId: string; outcome: 'approve' | 'reject' }
    | { kind: 'success'; message: string }
    | { kind: 'error'; message: string }
  >({ kind: 'idle' });

  // Track the latest mount so polling can no-op after unmount.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ── Resolve current-user id ─────────────────────────────
  // The Onboarding flow seeds the Zustand snapshot, but a deep-link into
  // the detail page can race that. If the snapshot is empty we fetch
  // /users/me lazily so role gating still works.
  const [currentUserId, setCurrentUserId] = useState<string | null>(
    userSnapshot?.id ?? null,
  );

  useEffect(() => {
    if (currentUserId) return;
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const { user } = await users.getMe();
        if (cancelled) return;
        setCurrentUserId(user.id);
        // Also seed the global snapshot so other pages benefit.
        const snapshot: UserProfile = user;
        setUser({
          id: snapshot.id,
          telegramId: snapshot.telegramId,
          username: snapshot.username,
          tonAddress: snapshot.tonAddress,
          reputationScore: snapshot.reputationScore,
          role: snapshot.role,
          isBanned: snapshot.isBanned,
        });
      } catch {
        // Swallow — the page can still render with role='neither'.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUserId, setUser]);

  // ── Tick countdown for the active deadline phase ────────
  //
  // The DeadlineBar drives its own ticker for sub-second updates, so this
  // outer tick is only needed to invalidate `now`-derived memos
  // (`fundingCountdown`, `fundingExpired`, deadline expiry checks). 30s
  // is plenty here. We tick whenever the deal is in any non-terminal
  // state that has an active deadline (created/funded/active/delivered).
  useEffect(() => {
    if (state.kind !== 'ready') return;
    const phasesWithDeadline: DealStatus[] = [
      'created',
      'funded',
      'active',
      'delivered',
    ];
    if (!phasesWithDeadline.includes(state.deal.status)) return;
    const id = window.setInterval(() => {
      setNow(Date.now());
    }, 30_000);
    return () => window.clearInterval(id);
  }, [state]);

  // ── Auto-dismiss the just-created banner after 8s ───────
  useEffect(() => {
    if (!showJustCreated) return;
    const id = window.setTimeout(() => setShowJustCreated(false), 8_000);
    return () => window.clearTimeout(id);
  }, [showJustCreated]);

  // ── Data loading ────────────────────────────────────────
  const loadDeal = useCallback(
    async (signal?: AbortSignal): Promise<Deal | null> => {
      try {
        const { deal } = await dealsApi.getDeal(shortId);
        if (signal?.aborted) return null;
        if (!mountedRef.current) return null;
        setState({ kind: 'ready', deal });
        return deal;
      } catch (err) {
        if (signal?.aborted) return null;
        if (!mountedRef.current) return null;
        if (isApiError(err) && (err.code === 'deal_not_found' || err.status === 404)) {
          setState({ kind: 'not-found' });
          return null;
        }
        const message =
          err instanceof Error ? err.message : t('dealDetail.error.network');
        setState({ kind: 'error', message });
        return null;
      }
    },
    [shortId, t],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    setState({ kind: 'loading' });
    void loadDeal(ctrl.signal);
    return () => ctrl.abort();
  }, [loadDeal]);

  const refresh = useCallback(async (): Promise<void> => {
    await loadDeal();
  }, [loadDeal]);

  // Extension fetch — shortId only, safe to define up here.
  const loadExtensions = useCallback(async (): Promise<void> => {
    try {
      const response = await dealsApi.listExtensions(shortId);
      if (!mountedRef.current) return;
      setExtensions(response.extensions);
    } catch {
      // Non-fatal — list simply won't render any extension rows.
    }
  }, [shortId]);

  useEffect(() => {
    if (state.kind !== 'ready') return;
    void loadExtensions();
  }, [state, loadExtensions]);

  const pendingExtension = useMemo(
    () => extensions.find((e) => e.status === 'pending') ?? null,
    [extensions],
  );

  // ── Background refresh for non-terminal deals ───────────
  //
  // Both parties need a silent auto-refresh so that state flips driven by
  // the blockchain worker (funding detected, auto-release on review
  // timeout, etc.) surface without the user pulling to refresh. We stop
  // polling the moment the status becomes terminal. The interval is a
  // relatively conservative 8 seconds to keep TonCenter/DB pressure low;
  // most transitions take 10-30 seconds end-to-end so a stale view is
  // corrected within ~1 tick.
  useEffect(() => {
    if (state.kind !== 'ready') return;
    if (TERMINAL_STATUSES.has(state.deal.status)) return;

    const id = window.setInterval(() => {
      if (!mountedRef.current) return;
      void loadDeal();
    }, BACKGROUND_REFRESH_INTERVAL_MS);

    return () => window.clearInterval(id);
  }, [state, loadDeal]);

  // ── Derived state ───────────────────────────────────────
  const deal = state.kind === 'ready' ? state.deal : null;
  const role: UserRole = useMemo(
    () => (deal ? deriveRole(currentUserId, deal) : 'neither'),
    [currentUserId, deal],
  );

  // ── Extension mutations (depend on `deal` + `role`) ────
  const submitExtension = useCallback(async (): Promise<void> => {
    if (!deal) return;
    const parsed = Number.parseInt(extensionHours, 10);
    if (!Number.isFinite(parsed) || parsed < 1 || parsed > 168) {
      setExtensionFlow({
        kind: 'error',
        message: t('dealDetail.extension.errorInvalidHours'),
      });
      return;
    }
    setExtensionFlow({ kind: 'submitting' });
    try {
      const trimmedReason = extensionReason.trim();
      await dealsApi.createExtension(deal.shortId, {
        hours: parsed,
        reason: trimmedReason.length > 0 ? trimmedReason : undefined,
      });
      haptics.success();
      const successMsg =
        role === 'buyer'
          ? t('dealDetail.extension.buyerSuccess')
          : t('dealDetail.extension.sellerSuccess');
      setExtensionFlow({ kind: 'success', message: successMsg });
      setExtensionHours('');
      setExtensionReason('');
      setExtensionModalOpen(false);
      await Promise.all([loadDeal(), loadExtensions()]);
    } catch (err) {
      haptics.error();
      const message =
        err instanceof Error ? err.message : t('dealDetail.extension.errorSubmit');
      setExtensionFlow({ kind: 'error', message });
    }
  }, [deal, extensionHours, extensionReason, loadDeal, loadExtensions, role, t]);

  const decideExtension = useCallback(
    async (extensionId: string, outcome: 'approve' | 'reject'): Promise<void> => {
      if (!deal) return;
      setExtensionFlow({ kind: 'deciding', extensionId, outcome });
      try {
        if (outcome === 'approve') {
          await dealsApi.approveExtension(deal.shortId, extensionId);
        } else {
          await dealsApi.rejectExtension(deal.shortId, extensionId);
        }
        haptics.success();
        setExtensionFlow({
          kind: 'success',
          message:
            outcome === 'approve'
              ? t('dealDetail.extension.approveSuccess')
              : t('dealDetail.extension.rejectSuccess'),
        });
        await Promise.all([loadDeal(), loadExtensions()]);
      } catch (err) {
        haptics.error();
        const message =
          err instanceof Error ? err.message : t('dealDetail.extension.errorSubmit');
        setExtensionFlow({ kind: 'error', message });
      }
    },
    [deal, loadDeal, loadExtensions, t],
  );

  const amountDisplay = useMemo(() => {
    if (!deal) return '— TON';
    try {
      return `${nanoToTon(BigInt(deal.amount), 4)} TON`;
    } catch {
      return '— TON';
    }
  }, [deal]);

  const platformFeeDisplay = useMemo(() => {
    if (!deal?.platformFee) return null;
    try {
      return `${nanoToTon(BigInt(deal.platformFee), 4)} TON`;
    } catch {
      return null;
    }
  }, [deal]);

  const fundingExpired = useMemo(() => {
    if (!deal) return false;
    if (deal.status !== 'created') return false;
    return new Date(deal.fundingDeadline).getTime() <= now;
  }, [deal, now]);

  /**
   * Pick the deadline relevant to the current FSM state.
   *
   *   created           → fundingDeadline (buyer must pay)
   *   funded / active   → deliveryDeadline (seller must deliver)
   *   delivered         → reviewDeadline (buyer must release before auto-release)
   *   any other status  → null (terminal / disputed — bar hidden)
   *
   * Returns the deadline ISO + the matching i18n key for the label and hint
   * so the JSX site stays declarative.
   */
  const activeDeadline = useMemo(():
    | {
        deadline: string;
        startedAt: string;
        labelKey: 'funding' | 'delivery' | 'review';
        forceExpired: boolean;
      }
    | null => {
    if (!deal) return null;
    const startedAt = deal.createdAt;
    if (deal.status === 'created') {
      return {
        deadline: deal.fundingDeadline,
        startedAt,
        labelKey: 'funding',
        forceExpired: false,
      };
    }
    if ((deal.status === 'funded' || deal.status === 'active') && deal.deliveryDeadline) {
      return {
        deadline: deal.deliveryDeadline,
        startedAt,
        labelKey: 'delivery',
        forceExpired: false,
      };
    }
    if (deal.status === 'delivered' && deal.reviewDeadline) {
      return {
        deadline: deal.reviewDeadline,
        startedAt,
        labelKey: 'review',
        forceExpired: false,
      };
    }
    return null;
  }, [deal]);

  // ── Polling after Fund tx ───────────────────────────────
  const startFundPolling = useCallback(async (): Promise<void> => {
    const startedAt = Date.now();
    let lastStatus: DealStatus | null = null;
    while (mountedRef.current && Date.now() - startedAt < FUND_POLL_TIMEOUT_MS) {
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, FUND_POLL_INTERVAL_MS);
      });
      if (!mountedRef.current) return;
      const fresh = await loadDeal();
      if (!fresh) return;
      lastStatus = fresh.status;
      if (fresh.status !== 'created') {
        haptics.success();
        setActionFlow({ kind: 'idle' });
        return;
      }
    }
    // Timed out: surface a soft warning and clear the spinner.
    if (mountedRef.current) {
      setActionFlow({ kind: 'idle' });
      if (lastStatus === 'created') {
        setActionError(t('dealDetail.error.pollTimeout'));
      }
    }
  }, [loadDeal, t]);

  // ── Fund flow ───────────────────────────────────────────
  const runFundFlow = useCallback(async (): Promise<void> => {
    if (!deal) return;
    if (role !== 'buyer') return;
    if (deal.status !== 'created') {
      setActionError(t('dealDetail.error.stateChanged'));
      return;
    }

    haptics.medium();
    setActionError(null);
    setActionFlow({ kind: 'fund-preparing' });

    let contractInfo: DealContractInfo;
    try {
      contractInfo = await dealsApi.getDealContractInfo(deal.shortId);
    } catch (err) {
      haptics.error();
      const message =
        isApiError(err) && err.code === 'deal_not_found'
          ? t('dealDetail.error.contractMissing')
          : err instanceof Error
            ? err.message
            : t('dealDetail.error.network');
      setActionError(message);
      setActionFlow({ kind: 'idle' });
      return;
    }

    if (contractInfo.status !== 'created') {
      setActionError(t('dealDetail.error.stateChanged'));
      setActionFlow({ kind: 'idle' });
      void refresh();
      return;
    }

    // Non-custodial runtime check (mandatory per CLAUDE.md).
    try {
      assertNonCustodialAddress(contractInfo.contractAddress, deal.contractAddress);
    } catch (err) {
      haptics.error();
      console.error('[deal-detail] non-custodial guard tripped', err);
      setActionError(t('dealDetail.error.nonCustodialViolation'));
      setActionFlow({ kind: 'idle' });
      return;
    }

    // Sanity-check the amount: must be a positive bigint.
    let nanoAmount: bigint;
    try {
      nanoAmount = BigInt(contractInfo.amount);
      if (nanoAmount <= 0n) {
        throw new Error('amount_non_positive');
      }
    } catch {
      setActionError(t('dealDetail.error.contractMissing'));
      setActionFlow({ kind: 'idle' });
      return;
    }

    // Wallet not connected → open the modal and bail. The user will tap
    // Fund again once they have a wallet.
    if (!tonConnectUI.account) {
      setActionError(t('dealDetail.error.connectWallet'));
      setActionFlow({ kind: 'fund-waiting-wallet' });
      try {
        await tonConnectUI.openModal();
      } catch {
        // Modal opens may fail in dev when there is no embed; ignore.
      }
      setActionFlow({ kind: 'idle' });
      return;
    }

    setActionFlow({ kind: 'fund-confirming' });

    try {
      await tonConnectUI.sendTransaction({
        validUntil: Math.floor(Date.now() / 1000) + TONCONNECT_VALID_FOR_SECONDS,
        messages: [
          {
            address: contractInfo.contractAddress,
            amount: nanoAmount.toString(),
          },
        ],
      });
    } catch (err) {
      haptics.error();
      const message =
        err instanceof Error
          ? err.message
          : t('dealDetail.error.transactionFailed');
      setActionError(message);
      setActionFlow({ kind: 'idle' });
      return;
    }

    // Tx sent — kick the backend's funding scan immediately so it
    // doesn't wait for the next periodic tick (10s), then start the
    // local on-chain polling. The kick is best-effort: if it fails
    // (network blip, rate limit), the periodic scan still catches the
    // deposit, the user just waits a few seconds longer.
    haptics.success();
    setActionError(null);
    if (deal) {
      try {
        await dealsApi.kickFundingCheck(deal.shortId);
      } catch {
        // Non-fatal — fall back to periodic scan.
      }
    }
    void startFundPolling();
  }, [deal, refresh, role, startFundPolling, t, tonConnectUI]);

  // ── Mark Delivered (seller) ─────────────────────────────
  const runMarkDelivered = useCallback(async (): Promise<void> => {
    if (!deal) return;
    haptics.medium();
    setActionError(null);
    setActionFlow({ kind: 'submitting' });
    try {
      const trimmed = noteInput.trim();
      await dealsApi.markDelivered(deal.shortId, trimmed.length > 0 ? { note: trimmed } : {});
      haptics.success();
      setConfirmKind(null);
      setNoteInput('');
      await refresh();
    } catch (err) {
      haptics.error();
      setActionError(
        err instanceof Error ? err.message : t('dealDetail.error.network'),
      );
    } finally {
      if (mountedRef.current) setActionFlow({ kind: 'idle' });
    }
  }, [deal, noteInput, refresh, t]);

  // ── Release Funds (buyer) ───────────────────────────────
  const runRelease = useCallback(async (): Promise<void> => {
    if (!deal) return;
    haptics.medium();
    setActionError(null);
    setActionFlow({ kind: 'submitting' });
    try {
      await dealsApi.releaseDeal(deal.shortId);
      haptics.success();
      setConfirmKind(null);
      await refresh();
    } catch (err) {
      haptics.error();
      setActionError(
        err instanceof Error ? err.message : t('dealDetail.error.network'),
      );
    } finally {
      if (mountedRef.current) setActionFlow({ kind: 'idle' });
    }
  }, [deal, refresh, t]);

  // ── Cancel ──────────────────────────────────────────────
  const runCancel = useCallback(async (): Promise<void> => {
    if (!deal) return;
    haptics.medium();
    setActionError(null);
    setActionFlow({ kind: 'submitting' });
    try {
      const trimmed = reasonInput.trim();
      await dealsApi.cancelDeal(deal.shortId, trimmed.length > 0 ? { reason: trimmed } : {});
      haptics.success();
      setConfirmKind(null);
      setReasonInput('');
      await refresh();
    } catch (err) {
      haptics.error();
      setActionError(
        err instanceof Error ? err.message : t('dealDetail.error.network'),
      );
    } finally {
      if (mountedRef.current) setActionFlow({ kind: 'idle' });
    }
  }, [deal, reasonInput, refresh, t]);

  // ── Inline secondary action: open dispute ───────────────
  // Ask for explicit confirmation before navigating — we had a live incident
  // where a user tapped this by mistake while meaning to release funds, which
  // froze the contract and required a manual arbiter refund.
  const confirmOpenDispute = useCallback(() => {
    if (!deal) return;
    haptics.medium();
    setConfirmKind('dispute');
  }, [deal]);

  const goToDispute = useCallback(() => {
    if (!deal) return;
    setConfirmKind(null);
    haptics.light();
    navigate(`/deals/${deal.shortId}/dispute`);
  }, [deal, navigate]);

  // ── Decide what the MainButton should do ────────────────
  const mainButtonConfig = useMemo(() => {
    if (!deal) return null;
    if (TERMINAL_STATUSES.has(deal.status)) return null;
    if (deal.status === 'disputed' || deal.status === 'resolved') return null;

    const submitting =
      actionFlow.kind === 'submitting' ||
      actionFlow.kind === 'fund-preparing' ||
      actionFlow.kind === 'fund-confirming';

    if (deal.status === 'created') {
      if (role === 'buyer') {
        const text =
          actionFlow.kind === 'fund-confirming'
            ? t('dealDetail.action.fundConfirming')
            : actionFlow.kind === 'fund-waiting-wallet'
              ? t('dealDetail.action.fundWaiting')
              : t('dealDetail.action.fund');
        return {
          text,
          onClick: () => void runFundFlow(),
          loading: submitting,
          disabled: submitting || fundingExpired,
        };
      }
      if (role === 'seller') {
        return {
          text: t('dealDetail.action.waitingForBuyer'),
          onClick: () => undefined,
          disabled: true,
        };
      }
      return null;
    }

    if (deal.status === 'funded' || deal.status === 'active') {
      if (role === 'seller') {
        return {
          text: t('dealDetail.action.markDelivered'),
          onClick: () => {
            haptics.light();
            setNoteInput('');
            setConfirmKind('deliver');
          },
          loading: submitting,
          disabled: submitting,
        };
      }
      if (role === 'buyer') {
        return {
          text: t('dealDetail.action.waitingForSeller'),
          onClick: () => undefined,
          disabled: true,
        };
      }
      return null;
    }

    if (deal.status === 'delivered') {
      if (role === 'buyer') {
        return {
          text: t('dealDetail.action.release'),
          onClick: () => {
            haptics.light();
            setConfirmKind('release');
          },
          loading: submitting,
          disabled: submitting,
        };
      }
      if (role === 'seller') {
        return {
          text: t('dealDetail.action.waitingForBuyer'),
          onClick: () => undefined,
          disabled: true,
        };
      }
      return null;
    }

    return null;
  }, [actionFlow.kind, deal, fundingExpired, role, runFundFlow, t]);

  useMainButton(mainButtonConfig);

  // ── Render branches ─────────────────────────────────────

  if (state.kind === 'loading') {
    return (
      <AppShell>
        <PageHeader title={t('dealDetail.header.title')} />
        <DealDetailSkeleton />
      </AppShell>
    );
  }

  if (state.kind === 'not-found') {
    return (
      <AppShell>
        <PageHeader title={t('dealDetail.header.title')} />
        <EmptyState
          title={t('dealDetail.error.notFound')}
          description={t('dealDetail.error.notFoundBody')}
          action={
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                haptics.light();
                navigate(ROUTES.deals);
              }}
            >
              {t('dealDetail.error.backToDeals')}
            </Button>
          }
        />
      </AppShell>
    );
  }

  if (state.kind === 'error') {
    return (
      <AppShell>
        <PageHeader title={t('dealDetail.header.title')} />
        <Card className="flex flex-col items-center gap-3 py-8 text-center">
          <div className="rounded-full bg-danger/15 p-3 text-danger">
            <AlertCircle className="h-6 w-6" aria-hidden />
          </div>
          <div className="space-y-1">
            <h3 className="text-base font-semibold text-text-primary">
              {t('dealDetail.error.network')}
            </h3>
            <p className="max-w-sm text-xs text-text-muted">{state.message}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => void refresh()}>
            {t('common.tryAgain')}
          </Button>
        </Card>
      </AppShell>
    );
  }

  // state.kind === 'ready'
  const dealReady = state.deal;
  const isReadOnly = TERMINAL_STATUSES.has(dealReady.status);
  const statusVariant = STATUS_BADGE_VARIANT[dealReady.status];

  const handleCopy = (): void => {
    setCopyFlash(true);
    window.setTimeout(() => setCopyFlash(false), 1500);
  };

  return (
    <AppShell>
      <PageHeader
        title={t('dealDetail.header.title')}
        description={dealReady.title}
      />

      {showJustCreated && (
        <Card className="flex items-start gap-3 border-success/40 bg-success/10">
          <PartyPopper
            className="mt-0.5 h-5 w-5 shrink-0 text-success"
            aria-hidden
          />
          <p className="text-xs leading-relaxed text-text-secondary">
            {t('dealDetail.header.justCreatedBanner')}
          </p>
        </Card>
      )}

      {/* ─── Header card ─────────────────────────────────── */}
      <Card className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-lg font-semibold text-text-primary">
            {dealReady.title}
          </h2>
          <Badge variant={statusVariant}>
            {t(`dealList.status.${dealReady.status}` as const)}
          </Badge>
        </div>

        <div className="flex items-center gap-2 text-xs text-text-muted">
          <span className="font-mono">#{dealReady.shortId}</span>
          <CopyButton
            value={dealReady.shortId}
            label={t('common.copy')}
            onCopied={handleCopy}
          />
          {copyFlash && (
            <span className="text-[10px] text-success">
              {t('common.copied')}
            </span>
          )}
        </div>

        <div className="mt-1 flex flex-col items-center gap-1 rounded-md border border-border/40 bg-bg-secondary/30 py-4">
          <span className="bg-gradient-to-r from-accent-gold to-accent-purple bg-clip-text text-3xl font-bold text-transparent">
            {amountDisplay}
          </span>
          {platformFeeDisplay && (
            <span className="text-[10px] uppercase tracking-wide text-text-muted">
              fee {platformFeeDisplay}
            </span>
          )}
        </div>

        {activeDeadline && (
          <DeadlineBar
            deadline={activeDeadline.deadline}
            startedAt={activeDeadline.startedAt}
            label={t(`dealDetail.deadline.${activeDeadline.labelKey}.label`)}
            hint={t(`dealDetail.deadline.${activeDeadline.labelKey}.hint`)}
            expiredLabel={t('dealDetail.deadline.expired')}
            forceExpired={
              activeDeadline.labelKey === 'funding' && fundingExpired
            }
          />
        )}
      </Card>

      {/* ─── Counterparty card ────────────────────────────── */}
      <Card className="flex flex-col gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          {t('dealDetail.counterparty.title')}
        </h3>
        <PartyRow
          role="buyer"
          userId={dealReady.buyerId}
          isCurrentUser={role === 'buyer'}
        />
        <PartyRow
          role="seller"
          userId={dealReady.sellerId}
          isCurrentUser={role === 'seller'}
        />
      </Card>

      {/* ─── Details card ─────────────────────────────────── */}
      <Card className="flex flex-col gap-1">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
          {t('dealDetail.details.sectionTitle')}
        </h3>
        <dl className="flex flex-col">
          <DetailRow label={t('dealDetail.details.description')} multiline>
            <span className="whitespace-pre-wrap text-sm text-text-secondary">
              {dealReady.description}
            </span>
          </DetailRow>
          {dealReady.terms && dealReady.terms.trim().length > 0 && (
            <DetailRow label={t('dealDetail.details.terms')} multiline>
              <span className="whitespace-pre-wrap text-sm text-text-secondary">
                {dealReady.terms}
              </span>
            </DetailRow>
          )}
          {dealReady.category && (
            <DetailRow label={t('dealDetail.details.category')}>
              <Badge variant="outline">{dealReady.category}</Badge>
            </DetailRow>
          )}
          <DetailRow label={t('dealDetail.details.createdAt')}>
            {formatDateTime(dealReady.createdAt)}
          </DetailRow>
          <DetailRow label={t('dealDetail.details.fundingDeadline')}>
            {formatDateTime(dealReady.fundingDeadline)}
          </DetailRow>
          {dealReady.deliveryDeadline && (
            <DetailRow label={t('dealDetail.details.deliveryDeadline')}>
              {formatDateTime(dealReady.deliveryDeadline)}
            </DetailRow>
          )}
          <DetailRow label={t('dealDetail.details.contractAddress')}>
            {dealReady.contractAddress ? (
              <div className="flex items-center justify-end gap-1">
                <a
                  href={explorerAddressUrl(dealReady.contractAddress)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-mono text-xs text-accent-purple hover:underline"
                >
                  {shortAddress(dealReady.contractAddress, 6, 6)}
                  <ArrowUpRight className="h-3 w-3" aria-hidden />
                </a>
                <CopyButton
                  value={dealReady.contractAddress}
                  label={t('common.copy')}
                  onCopied={handleCopy}
                />
              </div>
            ) : (
              <span className="text-xs text-text-muted">
                {t('dealDetail.details.contractNotDeployed')}
              </span>
            )}
          </DetailRow>
          {dealReady.deployTxHash && (
            <DetailRow label={t('dealDetail.details.txDeploy')}>
              <TxLink hash={dealReady.deployTxHash} />
            </DetailRow>
          )}
          {dealReady.fundTxHash && (
            <DetailRow label={t('dealDetail.details.txFund')}>
              <TxLink hash={dealReady.fundTxHash} />
            </DetailRow>
          )}
          {dealReady.releaseTxHash && (
            <DetailRow label={t('dealDetail.details.txRelease')}>
              <TxLink hash={dealReady.releaseTxHash} />
            </DetailRow>
          )}
          {dealReady.refundTxHash && (
            <DetailRow label={t('dealDetail.details.txRefund')}>
              <TxLink hash={dealReady.refundTxHash} />
            </DetailRow>
          )}
        </dl>
      </Card>

      {/* ─── Timeline ─────────────────────────────────────── */}
      <Timeline status={dealReady.status} deal={dealReady} />

      {/* ─── Dispute navigation banner ────────────────────── */}
      {(dealReady.status === 'disputed' || dealReady.status === 'resolved') && (
        <Card className="flex flex-col gap-3 border-danger/40 bg-danger/5">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-danger" aria-hidden />
            <h3 className="text-sm font-semibold text-text-primary">
              {t('dealDetail.disputeBanner.title')}
            </h3>
          </div>
          <p className="text-xs text-text-secondary">
            {t('dealDetail.disputeBanner.body')}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              haptics.light();
              navigate(`/deals/${dealReady.shortId}/dispute`);
            }}
            className="self-start border-danger/40 text-danger hover:bg-danger/10"
          >
            <ShieldAlert className="h-4 w-4" aria-hidden />
            {t('dealDetail.disputeBanner.cta')}
          </Button>
        </Card>
      )}

      {/* ─── Inline action error ──────────────────────────── */}
      {actionError !== null && (
        <Card className="flex items-start gap-2 border-danger/40 bg-danger/10" role="alert">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-danger" aria-hidden />
          <div className="flex-1">
            <p className="text-xs text-text-secondary">{actionError}</p>
          </div>
          <button
            type="button"
            onClick={() => setActionError(null)}
            className="text-[10px] uppercase text-text-muted hover:text-text-primary"
          >
            {t('common.close')}
          </button>
        </Card>
      )}

      {/* ─── Completed celebration ────────────────────────── */}
      {dealReady.status === 'completed' && (
        <Card className="flex flex-col items-center gap-2 border-success/40 bg-success/10 py-6 text-center">
          <PartyPopper className="h-8 w-8 text-success" aria-hidden />
          <h3 className="text-base font-semibold text-text-primary">
            {t('dealDetail.success.completedTitle')}
          </h3>
          <p className="max-w-sm text-xs text-text-secondary">
            {t('dealDetail.success.completedBody')}
          </p>
        </Card>
      )}

      {/* ─── Pending extension request card ───────────────── */}
      {pendingExtension && (role === 'buyer' || role === 'seller') && (
        <Card
          className={cn(
            'flex flex-col gap-3 border-accent-gold/40 bg-accent-gold/5',
          )}
        >
          <div className="flex items-start gap-2">
            <CalendarClock
              className="mt-0.5 h-4 w-4 shrink-0 text-accent-gold"
              aria-hidden
            />
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-text-primary">
                {t('dealDetail.extension.pendingCardTitle')}
              </h3>
              <p className="mt-0.5 text-xs text-text-secondary">
                {role === 'buyer'
                  ? t('dealDetail.extension.pendingCardBodyBuyer')
                  : t('dealDetail.extension.pendingCardBodySeller')}
              </p>
            </div>
          </div>

          <div className="rounded-md border border-border/40 bg-bg-tertiary/40 p-2.5 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-text-muted">
                {t('dealDetail.extension.pendingRequestBy')}
              </span>
              <span className="font-medium text-text-primary">
                {pendingExtension.requestedRole === 'seller'
                  ? t('dealList.role.seller')
                  : t('dealList.role.buyer')}
              </span>
            </div>
            <div className="mt-1 flex items-center justify-between">
              <span className="text-text-muted">+ saat</span>
              <span className="font-semibold text-accent-gold">
                {t('dealDetail.extension.pendingRequestHours', {
                  hours: pendingExtension.hours,
                })}
              </span>
            </div>
            {pendingExtension.reason && (
              <div className="mt-1.5 border-t border-border/30 pt-1.5">
                <span className="text-[10px] uppercase tracking-wide text-text-muted">
                  {t('dealDetail.extension.pendingRequestReason')}
                </span>
                <p className="mt-0.5 whitespace-pre-wrap text-text-secondary">
                  {pendingExtension.reason}
                </p>
              </div>
            )}
          </div>

          {/* Only the buyer can decide. Seller sees a disabled "bekliyor" state. */}
          {role === 'buyer' && pendingExtension.requestedRole === 'seller' && (
            <div className="flex gap-2">
              <Button
                variant="destructive"
                size="sm"
                className="flex-1"
                onClick={() => void decideExtension(pendingExtension.id, 'reject')}
                disabled={
                  extensionFlow.kind === 'deciding' &&
                  extensionFlow.extensionId === pendingExtension.id
                }
              >
                {extensionFlow.kind === 'deciding' &&
                extensionFlow.outcome === 'reject' &&
                extensionFlow.extensionId === pendingExtension.id
                  ? t('dealDetail.extension.rejecting')
                  : t('dealDetail.extension.reject')}
              </Button>
              <Button
                variant="gold"
                size="sm"
                className="flex-1"
                onClick={() => void decideExtension(pendingExtension.id, 'approve')}
                disabled={
                  extensionFlow.kind === 'deciding' &&
                  extensionFlow.extensionId === pendingExtension.id
                }
              >
                {extensionFlow.kind === 'deciding' &&
                extensionFlow.outcome === 'approve' &&
                extensionFlow.extensionId === pendingExtension.id
                  ? t('dealDetail.extension.approving')
                  : t('dealDetail.extension.approve')}
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* ─── Secondary inline actions ─────────────────────── */}
      {!isReadOnly && (
        <div className="flex flex-col gap-2">
          {/* Extension trigger: visible whenever the current deadline
              exists (funded/active/delivered). Buyer can extend outright;
              seller can only ask if there's no pending request already. */}
          {(dealReady.status === 'funded' ||
            dealReady.status === 'active' ||
            dealReady.status === 'delivered') &&
            (role === 'buyer' || role === 'seller') &&
            !(role === 'seller' && pendingExtension) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  haptics.light();
                  setExtensionHours('');
                  setExtensionReason('');
                  setExtensionFlow({ kind: 'idle' });
                  setExtensionModalOpen(true);
                }}
                className="border-accent-gold/40 text-accent-gold hover:bg-accent-gold/10"
              >
                <CalendarClock className="h-4 w-4" aria-hidden />
                {role === 'buyer'
                  ? t('dealDetail.extension.buyerButton')
                  : t('dealDetail.extension.sellerButton')}
              </Button>
            )}

          {(dealReady.status === 'active' || dealReady.status === 'delivered') &&
            (role === 'buyer' || role === 'seller') && (
              <Button
                variant="outline"
                size="sm"
                onClick={confirmOpenDispute}
                className="border-danger/40 text-danger hover:bg-danger/10"
              >
                <ShieldAlert className="h-4 w-4" aria-hidden />
                {t('dealDetail.action.openDispute')}
              </Button>
            )}

          {(dealReady.status === 'created' ||
            dealReady.status === 'funded' ||
            dealReady.status === 'active') &&
            (role === 'buyer' || role === 'seller') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  haptics.light();
                  setReasonInput('');
                  setConfirmKind('cancel');
                }}
              >
                {t('dealDetail.action.cancel')}
              </Button>
            )}
        </div>
      )}

      {/* ─── Wallet not connected hint ────────────────────── */}
      {dealReady.status === 'created' &&
        role === 'buyer' &&
        !tonConnectUI.account && (
          <Card className="flex items-start gap-2 border-accent-gold/40 bg-accent-gold/5">
            <Wallet
              className="mt-0.5 h-4 w-4 shrink-0 text-accent-gold"
              aria-hidden
            />
            <p className="text-xs text-text-secondary">
              {t('dealDetail.error.connectWallet')}
            </p>
          </Card>
        )}

      {/* ─── Confirmation dialogs ─────────────────────────── */}
      <Dialog
        open={confirmKind === 'release'}
        onOpenChange={(open) => {
          if (!open) setConfirmKind(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('dealDetail.action.releaseTitle')}</DialogTitle>
            <DialogDescription>
              {t('dealDetail.action.releaseConfirm')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmKind(null)}
              disabled={actionFlow.kind === 'submitting'}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="gold"
              onClick={() => void runRelease()}
              disabled={actionFlow.kind === 'submitting'}
            >
              {actionFlow.kind === 'submitting' ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : null}
              {t('dealDetail.action.release')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmKind === 'deliver'}
        onOpenChange={(open) => {
          if (!open) setConfirmKind(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('dealDetail.action.markDeliveredTitle')}</DialogTitle>
            <DialogDescription>
              {t('dealDetail.action.markDeliveredConfirm')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="deliver-note">{t('dealDetail.note.optional')}</Label>
            <textarea
              id="deliver-note"
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
              maxLength={1000}
              rows={3}
              placeholder={t('dealDetail.note.placeholder')}
              className={cn(
                'w-full resize-y rounded-md border border-border bg-bg-secondary/60 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple',
              )}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmKind(null)}
              disabled={actionFlow.kind === 'submitting'}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="default"
              onClick={() => void runMarkDelivered()}
              disabled={actionFlow.kind === 'submitting'}
            >
              {actionFlow.kind === 'submitting' ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : null}
              {t('dealDetail.action.markDelivered')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmKind === 'cancel'}
        onOpenChange={(open) => {
          if (!open) setConfirmKind(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('dealDetail.action.cancelTitle')}</DialogTitle>
            <DialogDescription>
              {t('dealDetail.action.cancelConfirm')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cancel-reason">{t('dealDetail.reason.optional')}</Label>
            <textarea
              id="cancel-reason"
              value={reasonInput}
              onChange={(e) => setReasonInput(e.target.value)}
              maxLength={1000}
              rows={3}
              placeholder={t('dealDetail.reason.placeholder')}
              className={cn(
                'w-full resize-y rounded-md border border-border bg-bg-secondary/60 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple',
              )}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmKind(null)}
              disabled={actionFlow.kind === 'submitting'}
            >
              {t('common.close')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => void runCancel()}
              disabled={actionFlow.kind === 'submitting'}
            >
              {actionFlow.kind === 'submitting' ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : null}
              {t('dealDetail.action.cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmKind === 'dispute'}
        onOpenChange={(open) => {
          if (!open) setConfirmKind(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-danger">
              <ShieldAlert className="h-5 w-5" aria-hidden />
              {t('dealDetail.action.openDisputeTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('dealDetail.action.openDisputeConfirm')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmKind(null)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={goToDispute}
            >
              <ShieldAlert className="h-4 w-4" aria-hidden />
              {t('dealDetail.action.openDisputeContinue')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Extension request modal ─────────────────────── */}
      <Dialog
        open={extensionModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setExtensionModalOpen(false);
            if (extensionFlow.kind === 'error') {
              setExtensionFlow({ kind: 'idle' });
            }
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-accent-gold">
              <CalendarClock className="h-5 w-5" aria-hidden />
              {role === 'buyer'
                ? t('dealDetail.extension.modalTitleBuyer')
                : t('dealDetail.extension.modalTitleSeller')}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="extension-hours">
                {t('dealDetail.extension.hoursLabel')}
              </Label>
              <Input
                id="extension-hours"
                type="number"
                inputMode="numeric"
                min={1}
                max={168}
                value={extensionHours}
                onChange={(e) => setExtensionHours(e.target.value)}
                placeholder={t('dealDetail.extension.hoursPlaceholder')}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="extension-reason">
                {t('dealDetail.extension.reasonLabel')}
              </Label>
              <textarea
                id="extension-reason"
                value={extensionReason}
                onChange={(e) => setExtensionReason(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder={t('dealDetail.extension.reasonPlaceholder')}
                className={cn(
                  'w-full resize-y rounded-md border border-border bg-bg-secondary/60 px-3 py-2 text-sm text-text-primary placeholder:text-text-muted',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple',
                )}
              />
            </div>
            {extensionFlow.kind === 'error' && (
              <p className="text-xs text-danger">{extensionFlow.message}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setExtensionModalOpen(false)}
              disabled={extensionFlow.kind === 'submitting'}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="gold"
              onClick={() => void submitExtension()}
              disabled={extensionFlow.kind === 'submitting'}
            >
              {extensionFlow.kind === 'submitting' ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <CalendarClock className="h-4 w-4" aria-hidden />
              )}
              {extensionFlow.kind === 'submitting'
                ? t('dealDetail.extension.submitting')
                : t('dealDetail.extension.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

// ─── Tx link ────────────────────────────────────────────────

interface TxLinkProps {
  hash: string;
}

function TxLink({ hash }: TxLinkProps): JSX.Element {
  return (
    <a
      href={explorerTxUrl(hash)}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 font-mono text-xs text-accent-purple hover:underline"
    >
      {shortAddress(hash, 6, 6)}
      <ArrowUpRight className="h-3 w-3" aria-hidden />
    </a>
  );
}
